import { format } from 'date-fns'
import { Calendar as CalendarIcon } from 'lucide-react'
import { DateRange } from 'react-day-picker'
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form'

import { cn } from '@/lib/utils'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

interface DateRangePickerProps<T extends FieldValues> {
    control: Control<T>
    name: FieldPath<T>
    label?: string
    placeholder?: string
    className?: string
    onChange?: (range: DateRange | undefined) => void
}

export function DateRangePicker<T extends FieldValues>({
    control,
    name,
    label,
    placeholder = 'Pick a date range',
    className,
    onChange,
}: DateRangePickerProps<T>) {
    return (
        <div className={cn('grid gap-2', className)}>
            {label && <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">{label}</label>}
            <Controller
                control={control}
                name={name}
                render={({ field }) => (
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                id="date"
                                variant={'outline'}
                                className={cn(
                                    'w-full justify-start text-left font-normal',
                                    !field.value && 'text-muted-foreground'
                                )}>
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {field.value?.from ? (
                                    field.value.to ? (
                                        <>
                                            {format(field.value.from, 'LLL dd, y')} -{' '}
                                            {format(field.value.to, 'LLL dd, y')}
                                        </>
                                    ) : (
                                        format(field.value.from, 'LLL dd, y')
                                    )
                                ) : (
                                    <span>{placeholder}</span>
                                )}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                                initialFocus
                                mode="range"
                                defaultMonth={field.value?.from}
                                selected={field.value}
                                onSelect={(val) => {
                                    field.onChange(val)
                                    onChange?.(val)
                                }}
                                numberOfMonths={2}
                            />
                        </PopoverContent>
                    </Popover>
                )}
            />
        </div>
    )
}
